import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle } from 'lucide-react';
import { BookingModal } from '../BookingModal';

const PricingSection = () => {
  const [billingPeriod, setBillingPeriod] = useState('yearly'); // Jahres-Abo vorausgewählt

  const products = {
    ida: {
      name: 'IDA Compliance',
      monthlyPrice: 34,
      yearlyPrice: 340,
      features: [
        'Spieler Selbsttest inklusive',
        'Alle IDA Basisfunktionen',
        'Risikoprofile & Reports',
        'E-Mail Support',
        'Detaillierte Analytics',
        'PDF-Export für Berichte'
      ],
      popular: true
    },
    timetracking: {
      name: 'Zeiterfassung',
      monthlyPrice: 39,
      yearlyPrice: 390,
      features: [
        'Bis zu 10 Mitarbeiter',
        'Zeiterfassung & Projektverfolgung',
        'Urlaubs-/Abwesenheitsverwaltung',
        'Genehmigungsworkflows',
        'Mobile Zeiterfassung',
        'Export & Reporting'
      ],
      popular: false
    },
    enterprise: {
      name: 'Enterprise',
      price: 'Individuell',
      features: [
        'Maßgeschneiderte Lösungen',
        'Unbegrenzte Nutzer',
        'Individuelle Workflows',
        'On-Premise Installation',
        'Persönlicher Support',
        'SLA-Garantien'
      ],
      isEnterprise: true
    }
  };

  const formatPrice = (price) => {
    return price.toFixed(2).replace('.', ',');
  };

  const calculateMonthlyPrice = (yearlyPrice) => {
    return Math.round((yearlyPrice / 12) * 100) / 100;
  };

  const calculateSavings = (monthlyPrice, yearlyPrice) => {
    return (monthlyPrice * 12) - yearlyPrice;
  };

  const calculateSavingsPercent = (monthlyPrice, yearlyPrice) => {
    return Math.round(((monthlyPrice * 12 - yearlyPrice) / (monthlyPrice * 12)) * 100);
  };

  return (
    <section id="pricing" className="py-20 bg-gray-100">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Preismodelle
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Compliance-Management und Zeiterfassung für Ihren Betrieb
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-teal-700 to-blue-500 mx-auto rounded-full mt-6"></div>
        </div>

        {/* Billing Period Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex items-center bg-gray-100 rounded-full p-1" style={{ height: '56px' }}>
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 ${
                billingPeriod === 'monthly'
                  ? 'bg-white text-gray-900 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monatlich
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-8 py-3 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 ${
                billingPeriod === 'yearly'
                  ? 'bg-white text-gray-900 shadow-md'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Jährlich
              <span className="bg-teal-700 text-white text-sm font-bold px-2 py-1 rounded-full">
                2 Monate gratis
              </span>
            </button>
          </div>
        </div>

        <p className="text-center text-gray-500 text-sm mb-8">
          Alle Preise zzgl. der gesetzlichen Mehrwertsteuer (19%)
        </p>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">

          {/* IDA Compliance Card */}
          <div className="relative">
            <div className="absolute -inset-2 bg-teal-400/20 rounded-lg animate-pulse blur-sm"></div>
            <Card className="relative hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-700 bg-gray-50 flex flex-col">
              {products.ida.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-teal-700 text-white px-5 py-1.5 rounded-full text-sm font-semibold shadow-md">
                  Beliebt
                </div>
              )}
              <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{products.ida.name}</h3>

              {/* Price */}
              <div className="mb-6">
                {billingPeriod === 'monthly' ? (
                  <>
                    <div className="text-5xl font-extrabold text-gray-900 leading-none">
                      €{formatPrice(products.ida.monthlyPrice)}
                      <span className="text-lg font-normal text-gray-600 ml-2">/Monat</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-5xl font-extrabold text-gray-900 leading-none">
                      €{formatPrice(products.ida.yearlyPrice)}
                      <span className="text-lg font-normal text-gray-600 ml-2">/Jahr</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      €{formatPrice(calculateMonthlyPrice(products.ida.yearlyPrice))}/Monat
                      <span className="text-gray-600"> zzgl. gesetzl. MwSt.</span>
                    </p>
                  </>
                )}
                {billingPeriod === 'monthly' && (
                  <p className="text-sm text-gray-600 mt-2">zzgl. gesetzl. MwSt.</p>
                )}
              </div>

              {/* Savings Block */}
              {billingPeriod === 'yearly' && (
                <div className="bg-teal-50 border border-teal-700 rounded-lg p-3 mb-6">
                  <p className="text-teal-800 font-semibold text-sm">
                    2 Monate bzw. €{formatPrice(calculateSavings(products.ida.monthlyPrice, products.ida.yearlyPrice))} / Jahr geschenkt.
                  </p>
                </div>
              )}

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-grow">
                {products.ida.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-4 group">
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                      <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-auto">
                <BookingModal selectedPlan={`ida-${billingPeriod}`}>
                  <Button className="w-full bg-teal-700 hover:bg-teal-800 text-white font-semibold py-3 rounded-lg transition-all duration-200 hover:shadow-lg">
                    Jetzt buchen
                  </Button>
                </BookingModal>
                <div className="text-center mt-3">
                  <Link to="/demo-anfrage" className="text-gray-600 hover:text-teal-700 underline">
                    Demo anfragen
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Zeiterfassung Card */}
          <Card className="relative hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-700 bg-gray-50 flex flex-col">
            <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{products.timetracking.name}</h3>

              {/* Price */}
              <div className="mb-6">
                {billingPeriod === 'monthly' ? (
                  <>
                    <div className="text-5xl font-extrabold text-gray-900 leading-none">
                      €{formatPrice(products.timetracking.monthlyPrice)}
                      <span className="text-lg font-normal text-gray-600 ml-2">/Monat</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-5xl font-extrabold text-gray-900 leading-none">
                      €{formatPrice(products.timetracking.yearlyPrice)}
                      <span className="text-lg font-normal text-gray-600 ml-2">/Jahr</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      €{formatPrice(calculateMonthlyPrice(products.timetracking.yearlyPrice))}/Monat
                      <span className="text-gray-600"> zzgl. gesetzl. MwSt.</span>
                    </p>
                  </>
                )}
                {billingPeriod === 'monthly' && (
                  <p className="text-sm text-gray-600 mt-2">zzgl. gesetzl. MwSt.</p>
                )}
              </div>

              {/* Savings Block */}
              {billingPeriod === 'yearly' && (
                <div className="bg-teal-50 border border-teal-700 rounded-lg p-3 mb-6">
                  <p className="text-teal-800 font-semibold text-sm">
                    2 Monate bzw. €{formatPrice(calculateSavings(products.timetracking.monthlyPrice, products.timetracking.yearlyPrice))} / Jahr geschenkt.
                  </p>
                </div>
              )}

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-grow">
                {products.timetracking.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-4 group">
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                      <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-auto">
                <BookingModal selectedPlan={`timetracking-${billingPeriod}`}>
                  <Button className="w-full bg-teal-700 hover:bg-teal-800 text-white font-semibold py-3 rounded-lg transition-all duration-200 hover:shadow-lg">
                    Jetzt buchen
                  </Button>
                </BookingModal>
                <div className="text-center mt-3">
                  <Link to="/demo-anfrage" className="text-gray-600 hover:text-teal-700 underline">
                    Demo anfragen
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Enterprise Card */}
          <Card className="relative hover:shadow-xl transition-all duration-300 border-2 border-gray-200 hover:border-teal-700 bg-gray-50 flex flex-col">
            <CardContent className="p-8 flex flex-col h-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">{products.enterprise.name}</h3>

              {/* Price */}
              <div className="mb-6">
                <div className="text-4xl font-extrabold text-teal-700 leading-none mb-2">
                  {products.enterprise.price}
                </div>
                <p className="text-sm text-gray-600 mt-2">Maßgeschneiderte Lösung</p>
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8 flex-grow">
                {products.enterprise.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-4 group">
                    <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                      <svg className="w-5 h-5 text-teal-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-auto">
                <Link to="/demo-anfrage">
                  <Button className="w-full bg-teal-700 hover:bg-teal-800 text-white font-semibold py-3 rounded-lg transition-all duration-200 hover:shadow-lg">
                    Kontakt aufnehmen
                  </Button>
                </Link>
                <div className="h-10"></div>
              </div>
            </CardContent>
          </Card>

        </div>
      </div>
    </section>
  );
};

export default PricingSection;
